import authApi from '@/api/authApi'
import PasswordField from '@/components/form-control/PasswordField'
import { PASSWORD_REGEX } from '@/constant/common'
import useUser from '@/features/auth/hooks/useUser'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import * as yup from 'yup'

interface ChangePasswordViewProps {
  closeModal: () => void
}

const schema = yup
  .object({
    currentPassword: yup
      .string()
      .required('Current password is required')
      .min(5, 'Minimum 5 characters')
      .max(50, 'Maximum 50 characters'),
    newPassword: yup
      .string()
      .required('New Password is required')
      .matches(
        PASSWORD_REGEX,
        'At least one uppercase letter, one lowercase letter, one number and one special character',
      ),
    confirmPassword: yup
      .string()
      .test('passwords-match', 'New password does not match', function (value) {
        return this.parent.newPassword === value
      })
      .required('Retype password is required')
      .matches(
        PASSWORD_REGEX,
        'At least one uppercase letter, one lowercase letter, one number and one special character',
      ),
  })
  .required()

const ChangePasswordView = ({ closeModal }: ChangePasswordViewProps) => {
  const inputRefSiteName = useRef<HTMLInputElement>(null)
  const [loadingButton, setLoadingButton] = useState(false)
  const { data: user } = useUser()

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const handleChangePassword = async (data: any) => {
    const userEmail = user.email

    if (!userEmail) {
      toast.error('Something went wrong')
      return
    }

    const payload = { ...data, email: userEmail }

    try {
      setLoadingButton(true)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await authApi.changePassword(payload)
      closeModal()
      toast.success('Your password has been changed successfully!')
    } catch (error: any) {
      if (error?.response?.status === 400)
        form.setError('currentPassword', {
          message: error?.response?.data?.Message,
        })
      if (error?.response?.status === 403)
        form.setError('newPassword', {
          message: error?.response?.data?.Message,
        })
      else {
        toast.error(error.message)
      }
    } finally {
      setLoadingButton(false)
    }
  }

  return (
    <form onSubmit={form.handleSubmit(handleChangePassword)}>
      <Stack
        sx={{
          backgroundColor: 'background.default',
          width: '378px',
          minHeight: '281px',
          padding: '30px',
          boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
        }}
      >
        <Stack>
          <Typography
            sx={{
              color: 'text.main',
              marginBottom: '15px',
              fontSize: '16px',
            }}
            variant="h3"
          >
            Change Password
          </Typography>
          <Box sx={{ marginBottom: '15px' }}>
            <PasswordField
              name="currentPassword"
              form={form}
              title="Current Password"
              placeholder="Current Password"
              showErrorMessage={true}
              inputRef={inputRefSiteName}
            />
          </Box>
          <Box sx={{ marginBottom: '15px' }}>
            <PasswordField
              name="newPassword"
              form={form}
              title="New Password"
              placeholder="New Password"
              showErrorMessage={true}
            />
          </Box>
          <Box sx={{ marginBottom: '15px' }}>
            <PasswordField
              name="confirmPassword"
              form={form}
              title="Confirm New Password"
              placeholder="Confirm New Password"
              showErrorMessage={true}
            />
          </Box>
          <Stack direction="row" sx={{ justifyContent: 'end', mt: '10px' }}>
            <Button
              variant="secondary"
              sx={{
                width: '99px',
                marginRight: '10px',
                maxHeight: '26px',
              }}
              onClick={() => closeModal()}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="primary"
              type="submit"
              size="small"
              loading={loadingButton}
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                width: '88px',
                maxHeight: '26px',
                borderRadius: '4px',
                '&:disabled': {
                  backgroundColor: 'grey.600',
                },
              }}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}

export default ChangePasswordView
